// net_flow renderer, split into two independently-placeable pieces so the page
// can pin the KPI tiles at the very top and drop the charts into the lower
// time-charts section:
//   - NetFlowKpis    — the 4-tile KPI row (net premium sign-coloured, etc.).
//   - NetFlowCharts  — cumulative (shared Baseline chart, two-tone) + the
//                      bespoke daily DivergingBarChart.
// NetFlowCard renders both stacked (used by the generic dispatch/tests).

import { useContext, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { UTCTimestamp } from 'lightweight-charts';

import { ChartTypeEnum } from '../../../../model/chart_type.enum';
import { PortfolioPerformanceChartType } from '../../../portfolio-performance/components/portfolio-perfomance-chart-type';
import { useThemeMode } from '../../../../providers/theme-provider';
import { getTokenValue, tokens } from '../../../../styles/tokens';
import {
  fmtCount,
  fmtNotional,
  fmtPercent,
  fmtRatio,
  fmtUsd,
  FlowMetric,
  resolveNotionalUnit,
} from '../../../../providers/options-flow/options-flow.helpers';
import {
  dilettaPanelSx,
  DILETTA_TITLE,
} from '../../../../components/v2/widgets/exotics-shared/diletta-chrome';
import {
  NetFlowKpis as NetFlowKpisData,
  NetFlowReport,
} from '../../../../providers/options-flow/types';
import { OptionsFlowContext } from '../../../../providers/options-flow/options-flow-provider';
import { WindowBadge } from '../window-badge';
import { FlowKpiTile, KpiSign } from '../flow-kpi-tile';
import { FlowMetricToggle } from '../flow-metric-toggle';
import { DivergingBarChart } from '../diverging-bar-chart';
import { FlowEmptyState } from '../flow-empty-state';

interface NetFlowCardProps {
  report: NetFlowReport;
}

// Cumulative net-premium as {time, value} for the Baseline series. The
// contract's dates are YYYY-MM-DD (UTC midnight); lightweight-charts wants a
// UTC epoch-seconds timestamp.
const toCumSeries = (report: NetFlowReport): { time: UTCTimestamp; value: number }[] =>
  report.series.daily.map((d) => ({
    time: (Date.parse(`${d.date}T00:00:00Z`) / 1000) as UTCTimestamp,
    value: d.cum,
  }));

const CardShell = ({
  title,
  caption,
  children,
}: {
  title: string;
  caption: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      // Platform widget chrome — same panel PP + carry-basis use, not the
      // PoC's near-black card.
      ...dilettaPanelSx,
      padding: '14px 16px 12px',
      marginBottom: '14px',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ fontSize: 12.5, fontWeight: 700, color: DILETTA_TITLE }}>{title}</Box>
      <Box sx={{ flexGrow: 1 }} />
      <WindowBadge />
    </Box>
    <Box sx={{ fontSize: 10.5, color: tokens.text.muted, margin: '2px 0 6px' }}>{caption}</Box>
    {children}
  </Box>
);

// The net-taker / total tiles follow the selected metric. One descriptor per
// metric — the kpis fields (net / total) and their labels together, so a rename
// or a new metric is a single edit and the four can't drift apart. The formatter
// stays separate (it closes over the notional unit at render). Only the net_flow
// tiles that HAVE a notional/trades sibling switch — the put/call ratio and
// block share are premium-inherent and stay put.
interface MetricTile {
  netField: keyof NetFlowKpisData;
  totalField: keyof NetFlowKpisData;
  netLabel: string;
  totalLabel: string;
}
const TILE: Record<FlowMetric, MetricTile> = {
  notional: {
    netField: 'net_notional_usd',
    totalField: 'total_notional_usd',
    netLabel: 'Net taker notional',
    totalLabel: 'Total notional traded',
  },
  premium: {
    netField: 'net_premium_usd',
    totalField: 'total_premium_usd',
    netLabel: 'Net taker premium',
    totalLabel: 'Total premium traded',
  },
  trades: {
    netField: 'net_trades',
    totalField: 'total_trades',
    netLabel: 'Net taker trades',
    totalLabel: 'Total trades',
  },
};

// KPI tiles only — pinned at the top of the page.
export const NetFlowKpis = ({ report }: NetFlowCardProps) => {
  const { kpis, meta } = report;
  // Selected metric — local state, defaulting to Notional (the client's primary
  // headline), same per-card selection pattern as ranked's sort. Value AND label
  // of the net-taker / total tiles track it; the shared toggle drives it.
  const [metric, setMetric] = useState<FlowMetric>('notional');

  const unit = resolveNotionalUnit(meta);
  const fmtFor: Record<FlowMetric, (v: number | null | undefined) => string> = {
    notional: (v) => fmtNotional(v, unit),
    premium: fmtUsd,
    trades: fmtCount,
  };

  const tile = TILE[metric];
  const netVal = kpis[tile.netField];
  const totalVal = kpis[tile.totalField];
  // Sign colour only when we have a value; an absent field renders neutral (—).
  const netSign: KpiSign = netVal == null ? 'neutral' : netVal >= 0 ? 'positive' : 'negative';

  // Venue degradation: hide the block-share tile on venues that don't tag blocks
  // (e.g. OKX) — the field would read a meaningless 0%.
  const { activeVenue } = useContext(OptionsFlowContext);
  const showBlocks = activeVenue?.blocks_supported !== false;
  const nTiles = showBlocks ? 4 : 3;

  return (
    <Box>
      <Box sx={{ mb: '10px' }}>
        <FlowMetricToggle active={metric} onSelect={setMetric} />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${nTiles}, 1fr)` },
          gap: '14px',
        }}
      >
        <FlowKpiTile
          label={tile.netLabel}
          value={fmtFor[metric](netVal)}
          sub={
            netVal == null ? undefined : `${netVal >= 0 ? 'net bought' : 'net sold'} over window`
          }
          sign={netSign}
        />
        <FlowKpiTile
          label={tile.totalLabel}
          value={fmtFor[metric](totalVal)}
          sub={meta.n_days != null ? `${meta.n_days} days` : undefined}
        />
        <FlowKpiTile
          label="Put / call flow ratio"
          value={fmtRatio(kpis.put_call_ratio)}
          sub={
            kpis.put_call_ratio == null
              ? 'no call premium'
              : `${kpis.put_call_ratio < 1 ? 'call-skewed' : 'put-skewed'} demand`
          }
        />
        {showBlocks && (
          <FlowKpiTile
            label="Block share"
            value={fmtPercent(kpis.block_share_pct)}
            sub="of premium via blocks"
          />
        )}
      </Box>
    </Box>
  );
};

// The two net_flow charts — placed in the lower time-charts section.
export const NetFlowCharts = ({ report }: NetFlowCardProps) => {
  const { resolvedMode } = useThemeMode();
  const cumSeries = useMemo(() => toCumSeries(report), [report]);
  const hasSeries = report.series.daily.length > 0;
  const buyHex = getTokenValue('flow.buy', resolvedMode);
  const sellHex = getTokenValue('flow.sell', resolvedMode);

  return (
    <Box>
      <CardShell
        title="Net taker premium — cumulative"
        caption="Blue above zero = net bought, red below = net sold. Real Deribit trades."
      >
        {hasSeries ? (
          <Box sx={{ height: 230 }}>
            <PortfolioPerformanceChartType
              type={ChartTypeEnum.Baseline}
              data={cumSeries}
              precision={0}
              showTooltip
              hideWatermark
              baselineTopColor={buyHex}
              baselineBottomColor={sellHex}
            />
          </Box>
        ) : (
          <FlowEmptyState height={230} />
        )}
      </CardShell>

      <CardShell
        title="Daily taker premium — bought vs sold"
        caption="Blue = takers paid (bought), red = takers received (sold); dot = net."
      >
        {hasSeries ? <DivergingBarChart daily={report.series.daily} /> : <FlowEmptyState />}
      </CardShell>
    </Box>
  );
};

// Combined — tiles + charts stacked. Used by the generic dispatch (renderReports)
// and its tests; the page uses NetFlowKpis / NetFlowCharts separately.
export const NetFlowCard = ({ report }: NetFlowCardProps) => (
  <Box>
    <Box sx={{ mb: '14px' }}>
      <NetFlowKpis report={report} />
    </Box>
    <NetFlowCharts report={report} />
  </Box>
);
